import { useState, useEffect, useCallback } from "react";
import { getPhysSummary, getPhysBatches, createPhysBatch, getPhysCards, blockPhysCard, archivePhysCard, downloadPhysBatchPdf, replacePhysCard, getState } from "@/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { QrScanner } from "./QrScanner";
import { CreditCard, Loader2, Download, Plus, Ban, Archive, Repeat, ScanLine } from "lucide-react";
import { toast } from "sonner";

const STATUS_LABEL = { free: "Frei", active: "Aktiv", blocked: "Gesperrt", replaced: "Ersetzt", archived: "Archiviert" };
const STATUS_COLOR = {
  free: "bg-slate-100 text-slate-600", active: "bg-emerald-100 text-emerald-700",
  blocked: "bg-rose-100 text-rose-700", replaced: "bg-amber-100 text-amber-800", archived: "bg-slate-200 text-slate-500",
};

async function downloadPdf(batchId, sheet, fallbackName) {
  try {
    const blob = await downloadPhysBatchPdf(batchId, sheet);
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob); a.download = fallbackName;
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(a.href);
  } catch (e) {
    toast.error(e?.response ? "PDF konnte nicht erstellt werden." : "Keine Verbindung. Bitte Internetverbindung prüfen.");
  }
}

const batchFileName = (b) => `${b.first_card_number}-${b.last_card_number}-a4.pdf`;

const stateSummary = (st) => {
  if (!st) return null;
  if (st.progress?.mode === "points_spend") return `${st.customer?.points ?? 0} Punkte`;
  return `${st.progress?.current ?? 0}/${st.progress?.target ?? 0} Stempel`;
};

export const PhysicalCardsTab = () => {
  const [summary, setSummary] = useState(null);
  const [batches, setBatches] = useState([]);
  const [cards, setCards] = useState([]);
  const [filter, setFilter] = useState("");
  const [q, setQ] = useState("");
  const [count, setCount] = useState(100);
  const [printVersion, setPrintVersion] = useState("single");
  const [busy, setBusy] = useState(false);
  const [pdfConfirm, setPdfConfirm] = useState(null);

  // replace flow
  const [replaceCard, setReplaceCard] = useState(null);
  const [replaceState, setReplaceState] = useState(null);
  const [replaceScan, setReplaceScan] = useState(null);
  const [replaceScannerOpen, setReplaceScannerOpen] = useState(false);
  const [replaceBusy, setReplaceBusy] = useState(false);

  const load = useCallback(async () => {
    const [s, b, c] = await Promise.all([
      getPhysSummary(), getPhysBatches(), getPhysCards({ ...(filter ? { status: filter } : {}), ...(q ? { q } : {}) }),
    ]);
    setSummary(s); setBatches(b); setCards(c);
  }, [filter, q]);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    setBusy(true);
    try {
      const b = await createPhysBatch(Number(count), printVersion);
      toast.success(`Kartensatz #${b.batch_number} erstellt (${b.first_card_number}–${b.last_card_number}).`);
      await load();
    } catch (e) { toast.error("Konnte Kartensatz nicht erstellen."); }
    finally { setBusy(false); }
  };

  const onPdfClick = (b) => {
    if ((b.used_count || 0) > 0) setPdfConfirm(b);
    else downloadPdf(b.id, 1, batchFileName(b));
  };
  const doPdfConfirmed = async () => {
    const b = pdfConfirm; setPdfConfirm(null);
    await downloadPdf(b.id, 1, batchFileName(b));
  };

  const setStatus = async (fn, id, msg) => { try { await fn(id); toast.success(msg); await load(); } catch (e) { toast.error("Aktion fehlgeschlagen."); } };

  // ---- replace handlers ----
  const startReplace = async (c) => {
    setReplaceScan(null); setReplaceState(null); setReplaceScannerOpen(false);
    setReplaceCard(c);
    if (c.customer_id) {
      try { setReplaceState(await getState(c.customer_id)); } catch (e) { /* points optional */ }
    }
  };
  const closeReplace = () => {
    setReplaceCard(null); setReplaceScan(null); setReplaceState(null); setReplaceScannerOpen(false);
  };
  const onReplaceScanned = (info) => { setReplaceScan(info); setReplaceScannerOpen(false); };
  const confirmReplace = async () => {
    if (!replaceCard || !replaceScan) return;
    setReplaceBusy(true);
    try {
      await replacePhysCard(replaceCard.id, replaceScan.secure_token);
      toast.success(`Karte ersetzt: ${replaceCard.card_number} → ${replaceScan.card_number}`);
      const oldC = replaceCard;
      closeReplace();
      await load();
      void oldC;
    } catch (e) {
      toast.error(e?.response?.data?.detail || (e?.response ? "Ersetzen fehlgeschlagen." : "Keine Verbindung. Bitte Internetverbindung prüfen."));
    } finally { setReplaceBusy(false); }
  };

  if (!summary) return <div className="py-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;

  return (
    <div data-testid="physical-cards-tab" className="space-y-6">
      {/* Summary */}
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-2.5">
        {[["total", "Gesamt"], ["free", "Frei"], ["active", "Aktiv"], ["blocked", "Gesperrt"], ["replaced", "Ersetzt"], ["archived", "Archiviert"]].map(([k, l]) => (
          <div key={k} data-testid={`phys-sum-${k}`} className="bg-white rounded-2xl ring-1 ring-slate-100 shadow-soft p-3 text-center">
            <p className="font-display font-extrabold text-2xl text-slate-900">{summary[k]}</p>
            <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wide">{l}</p>
          </div>
        ))}
      </div>

      {/* Create */}
      <div className="bg-white rounded-3xl ring-1 ring-slate-100 shadow-soft p-5">
        <p className="font-display font-extrabold text-lg text-slate-900 flex items-center gap-2"><Plus className="h-5 w-5" /> Neue Karten erzeugen</p>
        <p className="text-xs text-slate-400 mt-0.5">Erzeugt neue Kartennummern & QR-Codes (neuer Kartensatz).</p>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <span className="text-sm font-bold text-slate-500">Anzahl:</span>
          {[10, 25, 50, 100].map((n) => (
            <button key={n} data-testid={`phys-count-${n}`} onClick={() => setCount(n)}
              className={`rounded-xl px-3 py-2 text-sm font-bold press ${count === n ? "bg-[var(--bs-teal)] text-white" : "bg-slate-100 text-slate-600"}`}>{n}</button>
          ))}
          <Input data-testid="phys-count-custom" type="number" min="1" max="1000" value={count} onChange={(e) => setCount(e.target.value)} className="h-10 w-24 rounded-xl" />
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <span className="text-sm font-bold text-slate-500">Druckversion:</span>
          {[["single", "Einseitig"], ["double", "Doppelseitig"]].map(([v, l]) => (
            <button key={v} data-testid={`phys-print-${v}`} onClick={() => setPrintVersion(v)}
              className={`rounded-xl px-3 py-2 text-sm font-bold press ${printVersion === v ? "bg-[var(--bs-teal)] text-white" : "bg-slate-100 text-slate-600"}`}>{l}{v === "double" ? " (bald)" : ""}</button>
          ))}
        </div>
        <Button data-testid="phys-create-batch" onClick={create} disabled={busy} className="mt-4 rounded-xl font-display font-bold text-white press" style={{ background: "var(--bs-teal)" }}>
          {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <CreditCard className="mr-1 h-4 w-4" />} Kartensatz erstellen
        </Button>
      </div>

      {/* Batch list */}
      <div>
        <p className="text-[11px] uppercase tracking-wider text-slate-400 font-bold px-1 mb-1.5">Kartensätze</p>
        <div className="space-y-2">
          {batches.length === 0 && <p className="text-sm text-slate-400 px-1">Noch keine Kartensätze.</p>}
          {batches.map((b) => (
            <div key={b.id} data-testid={`phys-batch-${b.batch_number}`} className="bg-white rounded-2xl ring-1 ring-slate-100 shadow-soft p-4 flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="font-display font-extrabold text-slate-900">Kartensatz #{b.batch_number}</p>
                <p className="text-sm text-slate-500">{b.first_card_number} – {b.last_card_number} · {b.card_count} Karten</p>
              </div>
              <div className="flex gap-2">
                <Button data-testid={`phys-batch-pdf-${b.batch_number}`} onClick={() => onPdfClick(b)} variant="outline" className="rounded-xl font-bold press"><Download className="mr-1 h-4 w-4" /> PDF herunterladen</Button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Single cards */}
      <div>
        <p className="text-[11px] uppercase tracking-wider text-slate-400 font-bold px-1 mb-1.5">Einzelne Karten</p>
        <div className="flex flex-wrap gap-2 mb-2">
          <Input data-testid="phys-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Kartennummer oder Anzeigename" className="h-10 rounded-xl flex-1 min-w-[180px]" />
          {["", "free", "active", "blocked", "replaced", "archived"].map((s) => (
            <button key={s || "all"} data-testid={`phys-filter-${s || "all"}`} onClick={() => setFilter(s)}
              className={`rounded-xl px-3 py-2 text-xs font-bold press ${filter === s ? "bg-[var(--bs-teal)] text-white" : "bg-slate-100 text-slate-600"}`}>{s ? STATUS_LABEL[s] : "Alle"}</button>
          ))}
        </div>
        <div className="bg-white rounded-2xl ring-1 ring-slate-100 shadow-soft divide-y divide-slate-100 max-h-[420px] overflow-auto">
          {cards.length === 0 && <p className="text-sm text-slate-400 p-4">Keine Karten.</p>}
          {cards.map((c) => (
            <div key={c.id} data-testid={`phys-card-${c.card_number}`} className="flex items-center gap-3 px-4 py-3">
              <span className="font-display font-extrabold text-slate-800 w-24">{c.card_number}</span>
              <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${STATUS_COLOR[c.status]}`}>{STATUS_LABEL[c.status]}</span>
              <span className="text-sm text-slate-500 flex-1 truncate">{c.customer_name || "—"}</span>
              {(c.status === "active" || c.status === "blocked") && c.customer_id && (
                <button data-testid={`phys-replace-${c.card_number}`} onClick={() => startReplace(c)} title="Karte ersetzen" className="text-slate-400 hover:text-[var(--bs-teal)] press"><Repeat className="h-4 w-4" /></button>
              )}
              {(c.status === "active" || c.status === "free") && (
                <button data-testid={`phys-block-${c.card_number}`} onClick={() => setStatus(blockPhysCard, c.id, "Karte gesperrt.")} title="Sperren" className="text-slate-400 hover:text-rose-600 press"><Ban className="h-4 w-4" /></button>
              )}
              {c.status !== "archived" && (
                <button data-testid={`phys-archive-${c.card_number}`} onClick={() => setStatus(archivePhysCard, c.id, "Karte archiviert.")} title="Archivieren" className="text-slate-400 hover:text-slate-700 press"><Archive className="h-4 w-4" /></button>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* PDF re-download confirm (only when batch has used cards) */}
      <Dialog open={!!pdfConfirm} onOpenChange={(o) => !o && setPdfConfirm(null)}>
        <DialogContent data-testid="phys-pdf-confirm-modal" className="rounded-3xl max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-display">Kartensatz erneut herunterladen?</DialogTitle>
            <DialogDescription className="leading-relaxed">Dieser Kartensatz enthält bereits verwendete Karten. Ein erneuter Ausdruck erzeugt identische Kartennummern und QR-Codes und kann zu doppelten Karten führen.</DialogDescription>
          </DialogHeader>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" className="rounded-xl" onClick={() => setPdfConfirm(null)}>Abbrechen</Button>
            <Button data-testid="phys-pdf-confirm-download" className="rounded-xl text-white" style={{ background: "var(--bs-teal)" }} onClick={doPdfConfirmed}>Trotzdem herunterladen</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Replace flow */}
      <Dialog open={!!replaceCard && !replaceScannerOpen} onOpenChange={(o) => !o && closeReplace()}>
        <DialogContent data-testid="phys-replace-modal" className="rounded-3xl max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-display">Karte ersetzen</DialogTitle>
            <DialogDescription className="leading-relaxed">
              {replaceScan ? "Bitte prüfe die Angaben und bestätige den Austausch." : "Bitte eine freie Ersatzkarte scannen."}
            </DialogDescription>
          </DialogHeader>

          {/* old card context */}
          <div className="rounded-2xl bg-slate-50 ring-1 ring-slate-100 p-3 text-sm space-y-1">
            <div className="flex justify-between"><span className="text-slate-500">Alte Karte</span><span className="font-display font-extrabold text-slate-900" data-testid="phys-replace-old">{replaceCard?.card_number}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Kunde</span><span className="font-bold text-slate-800">{replaceCard?.customer_name || "—"}</span></div>
            {stateSummary(replaceState) && (
              <div className="flex justify-between"><span className="text-slate-500">Aktuell</span><span className="font-bold text-slate-800">{stateSummary(replaceState)}</span></div>
            )}
            {replaceScan && (
              <div className="flex justify-between pt-1 border-t border-slate-200 mt-1"><span className="text-slate-500">Neue Karte</span><span className="font-display font-extrabold text-[var(--bs-teal)]" data-testid="phys-replace-new">{replaceScan.card_number}</span></div>
            )}
          </div>

          {replaceScan
            ? (
              <>
                <p className="text-xs text-slate-500 text-center">Punkte, Belohnungen und Verlauf bleiben erhalten.</p>
                <div className="flex gap-2 justify-end">
                  <Button variant="outline" className="rounded-xl" disabled={replaceBusy} onClick={() => setReplaceScan(null)}>Abbrechen</Button>
                  <Button data-testid="phys-replace-confirm" className="rounded-xl text-white" style={{ background: "var(--bs-teal)" }} disabled={replaceBusy} onClick={confirmReplace}>
                    {replaceBusy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null} Ersetzen bestätigen
                  </Button>
                </div>
              </>
            )
            : (
              <div className="flex gap-2 justify-end">
                <Button variant="outline" className="rounded-xl" onClick={closeReplace}>Abbrechen</Button>
                <Button data-testid="phys-replace-scan" className="rounded-xl text-white" style={{ background: "var(--bs-teal)" }} onClick={() => setReplaceScannerOpen(true)}>
                  <ScanLine className="mr-1 h-4 w-4" /> Freie Ersatzkarte scannen
                </Button>
              </div>
            )}
        </DialogContent>
      </Dialog>

      <QrScanner
        open={replaceScannerOpen}
        mode="replace"
        onClose={() => setReplaceScannerOpen(false)}
        onResolved={() => {}}
        onReplaceScan={onReplaceScanned}
        contextTop={replaceCard ? (
          <div className="text-center text-white" data-testid="phys-replace-scan-context">
            <p className="text-white/60 text-[11px] uppercase tracking-wider font-bold">Karte ersetzen</p>
            <p className="text-sm mt-1">
              <span className="font-display font-extrabold">{replaceCard.card_number}</span>
              <span className="text-white/70"> · {replaceCard.customer_name || "—"}</span>
              {stateSummary(replaceState) ? <span className="text-white/70"> · {stateSummary(replaceState)}</span> : null}
            </p>
            <p className="text-white/60 text-xs mt-0.5">Bitte eine freie Ersatzkarte scannen.</p>
          </div>
        ) : null}
        contextBottom={(
          <Button data-testid="phys-replace-cancel-scan" onClick={closeReplace}
            className="rounded-xl font-bold press bg-white/15 hover:bg-white/25 text-white border border-white/20">
            Abbrechen
          </Button>
        )}
      />
    </div>
  );
};
